import { Box, TableBody, TableCell, TableCellProps, TableHead, TableRow, useTheme } from "@mui/material";
import { RPMTableHeaderRow } from "../../shared/table/RPMTableHeaderRow";
import { RPMTableHeaderCell } from "../../shared/table/RPMTableHeaderCell";
import { useAppSelector } from "../../../redux/hooks";
import { rdsState } from "../../../redux/rds/rdsSlice";
import { winState } from "../../../redux/win/winSlice";
import { NonRunnerScratchType, Win } from "../../../interfaces/dtos/win";
import { rdsRunner_Dto } from "../../../interfaces/dtos/rdsRunner";
import { RPMTableScrollable } from "../../shared/table/RPMTableScrollable";
import { commonColours } from "../../shared/commonConstants";
import { ScheduleWinRunnerHeading } from "./ScheduleWinRunnerHeading";

interface ScheduleWinRunnersProps {
    eventId: number;
    currentEventWin?: Win;
}

export const ScheduleWinRunners = ({
    eventId,
    currentEventWin
}: ScheduleWinRunnersProps) => {

    const rds = useAppSelector(rdsState);
    const win = useAppSelector(winState);
    const theme = useTheme();

    const colStickeySx = {
        position: "sticky",
        textWrap: 'nowrap',
        background: theme.palette.grey[200],
    }

    const col1Width = 70;

    const colHeaderCommon = { backgroundColor: theme.palette.grey[200], fontWeight: 'bold' }

    const col1HeaderSx = { ...colStickeySx, ...colHeaderCommon, left: 0, width: col1Width, zIndex: 6, textWrap: 'wrap' };
    const col2HeaderSx = { ...colStickeySx, ...colHeaderCommon, left: col1Width, zIndex: 6 };
    const col1Sx = { ...colStickeySx, left: 0, width: col1Width };
    const col2Sx = { ...colStickeySx, left: col1Width };

    const getSelection = (selectionId: number) => {
        return rds.rdsSelections.find((s) => s.id === selectionId);
    }

    const selections = rds.rdsSelections.filter((s) => s.eventId === eventId);
    const selectionIds = selections.map((s) => s.id);
    const runners = rds.rdsRunners.filter((r) => selectionIds.some((s) => s === r.id))
        .toSorted((a, b) => (
            (getSelection(a.id)!.number < getSelection(b.id)!.number ? -1 : 1)
        ));

    const eventWins = win.eventWins.filter((e) => e.eventId === eventId);

    const getTableCellProps = (eventWin: Win, index: number, forHeader?: boolean): TableCellProps => {
        const isFirstColumn = index === 0;
        return {
            align: "center",
            sx: {
                verticalAlign: "top",
                color: getItemColour(eventWin),
                backgroundColor: getItemBackColour(eventWin, isFirstColumn),
                fontWeight: forHeader || isFirstColumn ? "bold" : undefined
            }
        }
    }

    const getItemColour = (eventWin: Win) => {
        if (eventWin.calculated?.isSuspended) {
            return commonColours(theme).suspended;
        } else if (eventWin.calculated?.isDisplayOnly) {
            return commonColours(theme).displayOnly;
        } else if (eventWin.calculated?.isDistributed) {
            return commonColours(theme).distributed;
        } else {
            return undefined;
        }
    }

    const getItemBackColour = (eventWin: Win, isFirstColumn: boolean) => {
        if (isFirstColumn) {
            return commonColours(theme).firstColumnBackground;
        }
        if (eventWin.calculated?.isRejected || eventWin.calculated?.isSuspended) {
            return commonColours(theme).suspendedBackground;
        } else {
            return undefined;
        }
    }

    const getWinSelection = (eventWin: Win, runner: rdsRunner_Dto) => {
        const scratchType = getScratchType(runner, eventWin)
        if (scratchType && scratchType !== '(Res)') {
            return scratchType
        }
        const selection = eventWin.selections?.find((s) =>
            s.runnerIds?.some((r) => r.toString() === runner.id.toString())
        );
        return selection ? selection.priceFract : '';
    }

    const getScratchType = (runner: rdsRunner_Dto, winEvent?: Win) => {
        if (winEvent?.nonRunnerEntities && winEvent.nonRunnerEntities.length > 0) {
            const nonRunner = winEvent?.nonRunnerEntities.find(win => win.runnerId === runner.id.toString());
            if (nonRunner) {
                switch (nonRunner.scratchType) {
                    case NonRunnerScratchType.NONRUNNER:
                        return '(N/R)'
                    case NonRunnerScratchType.RESERVE_RUNNER:
                        return '(Res)'
                    case NonRunnerScratchType.WITHDRAWN:
                        return '(W/D)'
                    case NonRunnerScratchType.VACANT:
                        return '(Vac)'
                }
            }
        }
        return ''
    }

    return (
        <RPMTableScrollable
            id="table-runners"
            containerSxProps={{ maxHeight: 'calc(100vh - 260px)' }}
            tableProps={{ stickyHeader: true }}
        >
            <TableHead>
                <RPMTableHeaderRow>
                    <RPMTableHeaderCell sx={col1HeaderSx}><Box sx={{ width: `${col1Width}px` }}>Runner No</Box></RPMTableHeaderCell>
                    <RPMTableHeaderCell sx={col2HeaderSx}>Runner Name</RPMTableHeaderCell>
                    {eventWins.map((eventWin, i) => (
                        <ScheduleWinRunnerHeading
                            key={eventWin.timestamp}
                            eventWin={eventWin}
                            columnNo={i}
                            getTableCellProps={getTableCellProps}
                        />
                    ))}
                </RPMTableHeaderRow>
            </TableHead>
            <TableBody>
                <TableRow>
                    <TableCell sx={col1Sx}></TableCell>
                    <TableCell sx={col2Sx}></TableCell>
                    {eventWins.map((eventWin, i) => (
                        <TableCell
                            key={eventWin.timestamp}
                            id={`runner-total-${eventWin.version}`}
                            {...getTableCellProps(eventWin, i)}
                        >
                            {eventWin.fieldMoney}%
                        </TableCell>
                    ))}
                </TableRow>
                {runners.map((runner) => (
                    <TableRow key={runner.id}>
                        <TableCell sx={col1Sx}>{getSelection(runner.id)?.number}</TableCell>
                        <TableCell sx={col2Sx}>{runner.reserveName ? `${runner.reserveName} (Res)` : `${runner.name} ${getScratchType(runner, currentEventWin)}`}</TableCell>
                        {eventWins.map((eventWin, i) => (
                            <TableCell
                                key={eventWin.timestamp}
                                id={`runner-${eventWin.version}-${runner.id}`}
                                {...getTableCellProps(eventWin, i)}
                            >
                                {getWinSelection(eventWin, runner)}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </RPMTableScrollable>
    )
}
